/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import { 
  MapPin, 
  Truck, 
  Navigation, 
  Play, 
  Square, 
  Users, 
  Radio, 
  Activity, 
  Clock, 
  AlertTriangle,
  RefreshCw,
  Layers,
  Compass,
  Maximize,
  Minimize
} from 'lucide-react';
import { Folio, CostCenter, DriverLocation, UserProfile, Destination } from '../types.ts';
import { dataService, sanitizeCostCenterCoordinates } from '../lib/dataService.ts';

export interface PreviewDestination {
  name?: string;
  address?: string;
  lat: number;
  lng: number;
  originLat?: number;
  originLng?: number;
  originName?: string;
  estimatedKm?: number;
  routeNotes?: string;
}

interface LogisticsMapProps {
  folios: Folio[];
  costCenters: CostCenter[];
  currentUser: UserProfile;
  destinations?: Destination[];
  previewDestination?: PreviewDestination | null;
  hideSidebarConsole?: boolean;
}

// Monterrey - Saltillo highway points for simulation
const HIGHWAY_ROUTE_MOCK = [
  { lat: 25.4316, lng: -101.0062, status: 'Salida de Planta Saltillo' },
  { lat: 25.4520, lng: -100.9540, status: 'Autopista 57D - Cuesta' },
  { lat: 25.5015, lng: -100.8120, status: 'Caseta de Cobro Saltillo' },
  { lat: 25.5640, lng: -100.6750, status: 'Límite de Estado Coahuila-NL' },
  { lat: 25.6420, lng: -100.5210, status: 'Entronque Santa Catarina' },
  { lat: 25.6720, lng: -100.4120, status: 'Paso por Av. Morones Prieto' },
  { lat: 25.7180, lng: -100.3450, status: 'Ingreso a Gonzalitos' },
  { lat: 25.7550, lng: -100.3010, status: 'Llegada a CEDIS Monterrey' }
];

const LOCAL_ROUTE_MOCK = [
  { lat: 25.7820, lng: -100.4050, status: 'Salida de Patio Escobedo' },
  { lat: 25.7620, lng: -100.3550, status: 'Paso por Libramiento' },
  { lat: 25.7420, lng: -100.3120, status: 'Av. Universidad' },
  { lat: 25.7210, lng: -100.2750, status: 'San Nicolás - Av. Rómulo Garza' },
  { lat: 25.7010, lng: -100.2450, status: 'Llegada a Planta Guadalupe' }
];

export default function LogisticsMap({ 
  folios, 
  costCenters, 
  currentUser,
  destinations = [],
  previewDestination = null,
  hideSidebarConsole = false
}: LogisticsMapProps) {
  const [leafletReady, setLeafletReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<DriverLocation | null>(null);
  const [selectedFolioId, setSelectedFolioId] = useState<string>('');
  const [plateNumber, setPlateNumber] = useState<string>('TRK-580-NL');
  const [isSharingLive, setIsSharingLive] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Active route info for map card overlay
  const [activeRouteInfo, setActiveRouteInfo] = useState<{
    destName: string;
    estimatedKm: number;
    etaMins: number;
    etaClockTime: string;
    routeNotes?: string;
    originName: string;
    driverName?: string;
  } | null>(null);

  // Simulation states
  const [isSimulatingHighway, setIsSimulatingHighway] = useState(false);
  const [highwaySimIndex, setHighwaySimIndex] = useState(0);
  const [isSimulatingLocal, setIsSimulatingLocal] = useState(false);
  const [localSimIndex, setLocalSimIndex] = useState(0);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});
  const centerMarkersRef = useRef<Record<string, any>>({});
  const routePolylineRef = useRef<any>(null);
  const routeGlowRef = useRef<any>(null);
  const routeOriginMarkerRef = useRef<any>(null);
  const routeDestMarkerRef = useRef<any>(null);
  const watchIdRef = useRef<number | null>(null);
  const highwaySimIntervalRef = useRef<any>(null);
  const localSimIntervalRef = useRef<any>(null);

  // Filter active folios for transit driver selection
  // Shows only active folios in transit from pickup to CEDIS (RECOLECCION, ENTRADA_CEDIS) or CEDIS exit to plant (SALIDA_CEDIS)
  const activeFoliosForLogistics = folios.filter(f => 
    f.status === 'RECOLECCION' || 
    f.status === 'ENTRADA_CEDIS' || 
    f.status === 'SALIDA_CEDIS'
  );

  // Filter active drivers strictly in transit (excluding folios that are completed or in non-transit stages like F-102524)
  const activeTransitDrivers = React.useMemo(() => {
    const activeTransitFolioIds = new Set<string>();
    folios.forEach(f => {
      const statusStr = f.status as string;
      if (statusStr === 'RECOLECCION' || statusStr === 'ENTRADA_CEDIS' || statusStr === 'SALIDA_CEDIS') {
        activeTransitFolioIds.add(f.id.trim().toUpperCase());
      }
    });

    return driverLocations.filter(d => {
      if (!d.active || !d.latitude || !d.longitude) return false;

      // If driver is associated with a specific folioId
      if (d.folioId) {
        const fid = d.folioId.trim().toUpperCase();
        return activeTransitFolioIds.has(fid);
      }

      // If driver ID is formatted like unit-FOLIOID
      if (d.id && d.id.startsWith('unit-')) {
        const fid = d.id.replace('unit-', '').trim().toUpperCase();
        return activeTransitFolioIds.has(fid);
      }

      // If driverName is present without explicit folioId, check if driverName is assigned to an active transit folio
      if (d.driverName) {
        return folios.some(f => {
          const statusStr = f.status as string;
          const isTransit = statusStr === 'RECOLECCION' || statusStr === 'ENTRADA_CEDIS' || statusStr === 'SALIDA_CEDIS';
          return isTransit && f.driverName?.toLowerCase() === d.driverName.toLowerCase();
        });
      }

      return false;
    });
  }, [driverLocations, folios]);

  // 1. DYNAMICALLY LOAD LEAFLET CSS & JS
  useEffect(() => {
    if ((window as any).L) {
      setLeafletReady(true);
      return;
    }

    // Append Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    // Append Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = () => {
      setLeafletReady(true);
    };
    script.onerror = () => {
      setErrorMessage('Error al cargar la librería de mapas (Leaflet). Verifique su conexión de red.');
    };
    document.body.appendChild(script);

    return () => {
      // Keep scripts loaded for quick tab switching
    };
  }, []);

  // 2. REAL-TIME FIRESTORE SUBSCRIPTION FOR DRIVER LOCATIONS
  useEffect(() => {
    const unsubscribe = dataService.subscribeToDriverLocations((locations) => {
      setDriverLocations(locations);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // 3. INITIALIZE LEAFLET MAP
  useEffect(() => {
    if (!leafletReady || !mapContainerRef.current) return;

    const L = (window as any).L;
    if (!L) return;

    // Destroy existing map if any
    if (mapRef.current) {
      try {
        mapRef.current.off();
        mapRef.current.remove();
      } catch (e) {
        console.warn('Leaflet map remove warning:', e);
      }
      mapRef.current = null;
    }

    // Default center: Escobedo / Monterrey area
    const defaultCenter = [25.6866, -100.3161]; // Monterrey Centro
    const defaultZoom = 9;

    let map: any = null;
    try {
      map = L.map(mapContainerRef.current, {
        center: defaultCenter,
        zoom: defaultZoom,
        zoomControl: true,
        fadeAnimation: true,
        markerZoomAnimation: true
      });

      // Map Tile Layers 100% libres de API key y sin marcas de agua
      const osmStreets = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        subdomains: ['a', 'b', 'c'],
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
      });

      const esriStreets = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri'
      });

      const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri'
      });

      // Default active base layer (OpenStreetMap / Esri Streets)
      osmStreets.addTo(map);

      // Selector de capas integrado para el usuario (Calles, Esri y Satelital)
      try {
        L.control.layers(
          {
            'Calles (OpenStreetMap)': osmStreets,
            'Calles Claras (Esri)': esriStreets,
            'Satélite (Esri)': esriSatellite
          },
          undefined,
          { position: 'bottomright' }
        ).addTo(map);
      } catch (layerCtrlErr) {
        console.warn('Leaflet layer control warning:', layerCtrlErr);
      }
    } catch (err) {
      console.warn('Error initializing Leaflet map:', err);
      return;
    }

    mapRef.current = map;
    markersRef.current = {};
    centerMarkersRef.current = {};

    // Put Cost Centers / Plants as fixed landmarks with sanitized coordinates
    const filteredCostCenters = costCenters.map(sanitizeCostCenterCoordinates).filter((cc) => {
      if (!cc.lat || !cc.lng) return false;
      if (folios && folios.length === 1) {
        const f = folios[0];
        if (f.costCenterId && cc.id === f.costCenterId) return true;
        if (f.clientId && cc.clientId && cc.clientId === f.clientId) return true;
        if (f.destination && cc.name.toLowerCase().includes(f.destination.toLowerCase())) return true;
        return false;
      }
      return true;
    });

    filteredCostCenters.forEach((cc) => {
      try {
        const ccIcon = L.divIcon({
          className: 'custom-cc-icon',
          html: `
            <div class="flex flex-col items-center">
              <div class="bg-slate-900 text-white p-1 rounded-lg border-2 border-slate-700 shadow-md text-[9px] font-bold whitespace-nowrap flex items-center gap-1">
                <span>📍</span>
                <span>${cc.name.replace('Planta ', '').replace('Taller de ', '')}</span>
              </div>
              <div class="w-1.5 h-1.5 bg-slate-900 border border-slate-700 -mt-1 rounded-full"></div>
            </div>
          `,
          iconSize: [120, 30],
          iconAnchor: [60, 25]
        });

        const ccMarker = L.marker([cc.lat, cc.lng], { icon: ccIcon })
          .addTo(map)
          .bindPopup(`
            <div class="p-1.5 font-sans space-y-1 text-slate-900">
              <h5 class="font-black text-xs uppercase border-b border-gray-100 pb-1">📍 ${cc.name}</h5>
              <p class="text-[10px] text-gray-500"><strong>Encargado:</strong> ${cc.manager}</p>
              <p class="text-[10px] text-gray-500"><strong>Dirección:</strong> ${cc.address}</p>
            </div>
          `);
        
        centerMarkersRef.current[cc.id] = ccMarker;
      } catch (e) {
        console.warn('Error placing cost center marker:', e);
      }
    });

    // Add CSS for pulse animations if not already present
    if (!document.getElementById('leaflet-custom-pulse-style')) {
      const style = document.createElement('style');
      style.id = 'leaflet-custom-pulse-style';
      style.innerHTML = `
        @keyframes pulseRing {
          0% { transform: scale(0.6); opacity: 0.9; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        .pulsing-ring {
          position: absolute;
          width: 32px;
          height: 32px;
          background-color: #22c55e;
          border-radius: 50%;
          animation: pulseRing 1.8s infinite ease-out;
          z-index: -1;
        }
        .driver-avatar-circle {
          width: 32px;
          height: 32px;
          background-color: #bc1100;
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 11px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.off();
          mapRef.current.remove();
        } catch (e) {
          console.warn('Leaflet cleanup error on unmount:', e);
        }
        mapRef.current = null;
      }
      markersRef.current = {};
      centerMarkersRef.current = {};
      routePolylineRef.current = null;
      routeGlowRef.current = null;
      routeOriginMarkerRef.current = null;
      routeDestMarkerRef.current = null;
    };
  }, [leafletReady, costCenters]);

  // 4. MAP DATA SYNCHRONIZATION WITH DRIVER LOCATIONS STATE
  useEffect(() => {
    if (!leafletReady || !mapRef.current) return;

    const L = (window as any).L;
    if (!L) return;

    const currentMap = mapRef.current;
    const currentMarkers = markersRef.current;

    // Track active driver IDs in the database
    const activeIds = new Set<string>();

    let displayDrivers: DriverLocation[] = [];

    if (folios && folios.length === 1) {
      const targetFolio = folios[0];
      const statusStr = targetFolio.status as string;
      const isTransitStage = 
        statusStr === 'RECOLECCION' || 
        statusStr === 'ENTRADA_CEDIS' || 
        statusStr === 'SALIDA_CEDIS';

      if (isTransitStage) {
        // Look for real-time driver matching this exact folio
        const match = driverLocations.find(d => 
          d.active && (
            (d.folioId && d.folioId === targetFolio.id) ||
            (d.driverName && targetFolio.driverName && d.driverName.toLowerCase() === targetFolio.driverName.toLowerCase()) ||
            d.id === targetFolio.id
          )
        );

        if (match) {
          displayDrivers = [{ ...match, folioId: targetFolio.id }];
        } else {
          // Dedicated single-unit marker for this specific folio
          let dLat = targetFolio.currentLat ? Number(targetFolio.currentLat) : (targetFolio.originLat ? Number(targetFolio.originLat) : 25.7820);
          let dLng = targetFolio.currentLng ? Number(targetFolio.currentLng) : (targetFolio.originLng ? Number(targetFolio.originLng) : -100.4050);

          if (previewDestination && previewDestination.lat && previewDestination.lng) {
            const oLat = previewDestination.originLat || 25.7820;
            const oLng = previewDestination.originLng || -100.4050;
            const targetLat = previewDestination.lat;
            const targetLng = previewDestination.lng;

            let ratio = 0.5;
            if (targetFolio.status === 'RECOLECCION' || targetFolio.status === 'ENTRADA_CEDIS') ratio = 0.1;
            else if (targetFolio.status === 'SALIDA_CEDIS') ratio = 0.65;
            else if (targetFolio.status === 'INSPECCION') ratio = 0.85;

            dLat = oLat + (targetLat - oLat) * ratio;
            dLng = oLng + (targetLng - oLng) * ratio;
          }

          displayDrivers = [{
            id: `unit-${targetFolio.id}`,
            driverName: targetFolio.driverName || 'Operador Logístico ISN',
            driverEmail: 'operador@grupoisn.com',
            latitude: dLat,
            longitude: dLng,
            speed: targetFolio.status === 'SALIDA_CEDIS' ? 78 : 0,
            lastUpdated: new Date().toISOString(),
            active: true,
            plateNumber: 'TRK-SNF',
            folioId: targetFolio.id,
            statusText: `Unidad asignada a Folio #${targetFolio.id}`
          }];
        }
      }
    } else if (folios && folios.length > 1) {
      const activeFolioMap = new Map<string, Folio>();
      folios.forEach(f => {
        const statusStr = f.status as string;
        const isTransitStage = 
          statusStr === 'RECOLECCION' || 
          statusStr === 'ENTRADA_CEDIS' || 
          statusStr === 'SALIDA_CEDIS';
        if (isTransitStage) {
          activeFolioMap.set(f.id.trim().toUpperCase(), f);
        }
      });

      displayDrivers = driverLocations.filter(d => {
        if (!d.latitude || !d.longitude || !d.active) return false;
        
        // If driver specifies a folioId, it MUST be in activeFolioMap (transit stages only)
        if (d.folioId) {
          const fid = d.folioId.trim().toUpperCase();
          if (!activeFolioMap.has(fid)) return false;
          return true;
        }

        // If driver ID is formatted like unit-FOLIOID
        if (d.id && d.id.startsWith('unit-')) {
          const fid = d.id.replace('unit-', '').trim().toUpperCase();
          if (!activeFolioMap.has(fid)) return false;
          return true;
        }

        // If driverName is provided without explicit folioId, check if driver is assigned to a transit folio
        if (d.driverName) {
          return Array.from(activeFolioMap.values()).some(f => f.driverName?.toLowerCase() === d.driverName.toLowerCase());
        }

        return false;
      });
    } else {
      displayDrivers = [];
    }

    displayDrivers.forEach((driver) => {
      activeIds.add(driver.id);
      const latlng = [driver.latitude, driver.longitude];
      const initials = driver.driverName
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

      // Check if driver marker already exists
      if (currentMarkers[driver.id]) {
        try {
          // Smoothly transition marker position
          if (currentMarkers[driver.id]._map) {
            currentMarkers[driver.id].setLatLng(latlng);
            const popup = currentMarkers[driver.id].getPopup();
            if (popup) {
              popup.setContent(getPopupHTML(driver));
            }
          }
        } catch (e) {
          console.warn('Error updating driver marker:', e);
        }
      } else {
        const totalFoliosCount = driver.folioIds?.length || driver.encapsulatedFolios?.length || (driver.folioId ? 1 : 0);
        const countPill = totalFoliosCount > 1 
          ? `<div class="absolute -top-1.5 -left-1.5 bg-indigo-600 border-2 border-white text-white font-black text-[9px] w-5 h-5 rounded-full shadow-md flex items-center justify-center font-mono z-20">${totalFoliosCount}</div>`
          : '';

        try {
          // Create WhatsApp-style custom HTML avatar marker with glowing green pulse
          const driverIcon = L.divIcon({
            className: 'custom-driver-icon relative flex items-center justify-center',
            html: `
              <div class="relative flex items-center justify-center w-[36px] h-[36px]">
                <div class="pulsing-ring"></div>
                ${countPill}
                <div class="driver-avatar-circle" style="background-color: ${driver.id.includes('sim-') ? '#1e293b' : '#bc1100'}">
                  ${initials}
                </div>
                <div class="absolute -bottom-1 -right-1 bg-green-500 border border-white text-white p-0.5 rounded-full shadow-xs flex items-center justify-center w-4 h-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-truck"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><polyline points="14 10 20 10 23 14 23 17 14 17"/><circle cx="7.5" cy="18.5" r="2.5"/><circle cx="17" cy="18.5" r="2.5"/></svg>
                </div>
              </div>
            `,
            iconSize: [36, 36],
            iconAnchor: [18, 18]
          });

          const marker = L.marker(latlng, { icon: driverIcon, zIndexOffset: 1000 })
            .addTo(currentMap)
            .bindPopup(getPopupHTML(driver));

          currentMarkers[driver.id] = marker;
        } catch (e) {
          console.warn('Error adding driver marker:', e);
        }
      }
    });

    // Remove stale drivers no longer in displayDrivers
    Object.keys(currentMarkers).forEach((id) => {
      if (!activeIds.has(id)) {
        try {
          if (currentMap && currentMarkers[id]) {
            currentMap.removeLayer(currentMarkers[id]);
          }
        } catch (e) {
          console.warn('Error removing marker:', e);
        }
        delete currentMarkers[id];
      }
    });

    // If driver is selected in list, fly to them (resolved with latest real-time coordinate)
    const activeSelectedDriver = selectedDriver ? displayDrivers.find(d => d.id === selectedDriver.id) : null;
    if (activeSelectedDriver && currentMarkers[activeSelectedDriver.id]) {
      try {
        const marker = currentMarkers[activeSelectedDriver.id];
        if (marker && marker._map) {
          const latlng = marker.getLatLng();
          if (latlng) {
            currentMap.setView(latlng, 13, { animate: true, duration: 1.5 });
            marker.openPopup();
          }
        }
      } catch (e) {
        console.warn('Error centering on selected driver:', e);
      }
    }
  }, [driverLocations, leafletReady, selectedDriver, folios, previewDestination]);

  // State for device GPS location
  const [deviceLocation, setDeviceLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Get device GPS location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setDeviceLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
        },
        (err) => {
          console.log("Device GPS location not available or permission pending:", err?.message);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, []);

  // 4b. ROUTE POLYLINE & REAL-TIME ETA GENERATION EFFECT WITH REAL ROAD GEOMETRY
  useEffect(() => {
    if (!leafletReady || !mapRef.current) return;

    let isMounted = true;
    const L = (window as any).L;
    if (!L) return;

    const currentMap = mapRef.current;

    // Clear previous route elements if any
    try {
      if (routePolylineRef.current && currentMap) {
        currentMap.removeLayer(routePolylineRef.current);
      }
    } catch (e) {}
    routePolylineRef.current = null;

    try {
      if (routeGlowRef.current && currentMap) {
        currentMap.removeLayer(routeGlowRef.current);
      }
    } catch (e) {}
    routeGlowRef.current = null;

    try {
      if (routeOriginMarkerRef.current && currentMap) {
        currentMap.removeLayer(routeOriginMarkerRef.current);
      }
    } catch (e) {}
    routeOriginMarkerRef.current = null;

    try {
      if (routeDestMarkerRef.current && currentMap) {
        currentMap.removeLayer(routeDestMarkerRef.current);
      }
    } catch (e) {}
    routeDestMarkerRef.current = null;

    // Determine target destination & origin
    let destLat: number | null = null;
    let destLng: number | null = null;
    let destName = '';
    
    // Default official shipment origin: CEDIS Escobedo
    let originLat = 25.7820;
    let originLng = -100.4050;
    let originName = '🏢 CEDIS Escobedo';
    let routeNotes = '';
    let kmOverride: number | undefined = undefined;
    let activeDriverName: string | undefined = undefined;

    if (previewDestination && previewDestination.lat && previewDestination.lng) {
      destLat = previewDestination.lat;
      destLng = previewDestination.lng;
      destName = previewDestination.name || previewDestination.address || 'Destino Registrado';
      routeNotes = previewDestination.routeNotes || '';
      kmOverride = previewDestination.estimatedKm;
      if (previewDestination.originLat && previewDestination.originLng) {
        originLat = previewDestination.originLat;
        originLng = previewDestination.originLng;
        originName = previewDestination.originName || originName;
      }
    } else if (folios && folios.length === 1) {
      const f = folios[0];
      const statusStr = f.status as string;
      const isFinished = 
        statusStr === 'ENTREGA_PLANTA' || 
        statusStr === 'ENTREGADO' || 
        statusStr === 'COMPLETADO' || 
        statusStr === 'FINALIZADO' || 
        statusStr === 'DEVOLUCION' || 
        statusStr === 'CANCELADO';

      if (isFinished) {
        setActiveRouteInfo(null);
        return;
      }

      if (f.destinationLat && f.destinationLng) {
        destLat = f.destinationLat;
        destLng = f.destinationLng;
      } else if (f.destinationId && destinations) {
        const match = destinations.find(d => d.id === f.destinationId);
        if (match) {
          destLat = match.lat;
          destLng = match.lng;
          routeNotes = match.routeNotes || '';
          kmOverride = match.estimatedKm;
        }
      } else if (f.destination && destinations) {
        const match = destinations.find(d => 
          d.address.toLowerCase().includes(f.destination!.toLowerCase()) || 
          d.name.toLowerCase().includes(f.destination!.toLowerCase())
        );
        if (match) {
          destLat = match.lat;
          destLng = match.lng;
          routeNotes = match.routeNotes || '';
          kmOverride = match.estimatedKm;
        }
      }

      // Fallback search in costCenters or name keywords
      if (!destLat || !destLng) {
        const ccMatch = costCenters.find(cc => 
          cc.id === f.costCenterId || cc.name.toLowerCase().includes((f.destination || '').toLowerCase())
        );
        if (ccMatch && ccMatch.lat && ccMatch.lng) {
          destLat = ccMatch.lat;
          destLng = ccMatch.lng;
        } else if (f.destination) {
          const lower = f.destination.toLowerCase();
          if (lower.includes('saltillo')) {
            destLat = 25.4316;
            destLng = -101.0062;
          } else if (lower.includes('ramos')) {
            destLat = 25.5420;
            destLng = -100.9310;
          } else if (lower.includes('apodaca')) {
            destLat = 25.7800;
            destLng = -100.1800;
          } else if (lower.includes('santa catarina')) {
            destLat = 25.6700;
            destLng = -100.4600;
          }
        }
      }

      destName = f.destination || f.clientName || 'Planta Destino';
      routeNotes = f.routeNotes || routeNotes;
      kmOverride = f.estimatedKm || kmOverride;
      activeDriverName = f.driverName;

      // Check if driver is transmitting live GPS for this folio
      const driverObj = driverLocations.find(d => (d.folioId && d.folioId === f.id) || (d.active && d.driverName === f.driverName));
      if (driverObj && driverObj.latitude && driverObj.longitude) {
        originLat = driverObj.latitude;
        originLng = driverObj.longitude;
        originName = `🚚 Chofer ${driverObj.driverName} (GPS en Vivo)`;
      } else if (f.origin) {
        originName = f.origin;
      }
    }

    if (!destLat || !destLng) {
      setActiveRouteInfo(null);
      return;
    }

    // Helper: Generate realistic curved highway path if OSRM is offline
    const generateRealisticRoadPath = (lat1: number, lng1: number, lat2: number, lng2: number): [number, number][] => {
      const points: [number, number][] = [];
      const numSegments = 16;
      const dx = lng2 - lng1;
      const dy = lat2 - lat1;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const perpX = -dy / dist;
      const perpY = dx / dist;

      for (let i = 0; i <= numSegments; i++) {
        const t = i / numSegments;
        const baseLat = lat1 + t * (lat2 - lat1);
        const baseLng = lng1 + t * (lng2 - lng1);
        
        // S-curve highway offset algorithm
        const offsetMag = Math.sin(t * Math.PI * 1.5) * (dist * 0.12);
        const lat = baseLat + perpY * offsetMag;
        const lng = baseLng + perpX * offsetMag;
        points.push([lat, lng]);
      }
      return points;
    };

    // Calculate initial estimated distance
    const R = 6371;
    const dLat = (destLat - originLat) * Math.PI / 180;
    const dLon = (destLng - originLng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(originLat * Math.PI / 180) * Math.cos(destLat * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const straightKm = R * c;
    const defaultEstimatedKm = kmOverride || Math.round(straightKm * 1.25 * 10) / 10;
    const defaultEtaMins = Math.max(10, Math.round((defaultEstimatedKm / 65) * 60));

    // Fetch real turn-by-turn road geometry from OSRM Routing Engine
    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${originLng},${originLat};${destLng},${destLat}?overview=full&geometries=geojson`;

    const loadAndRenderRoute = async () => {
      let pathCoords: [number, number][] = [];
      let calculatedKm = defaultEstimatedKm;
      let calculatedMins = defaultEtaMins;

      try {
        const res = await fetch(osrmUrl);
        if (res.ok) {
          const data = await res.json();
          if (data.routes && data.routes[0]) {
            const routeData = data.routes[0];
            const rawCoords: [number, number][] = routeData.geometry.coordinates; // [lng, lat]
            pathCoords = rawCoords.map(coord => [coord[1], coord[0]]); // Leaflet wants [lat, lng]
            
            if (routeData.distance) {
              calculatedKm = Math.round((routeData.distance / 1000) * 10) / 10;
            }
            if (routeData.duration) {
              calculatedMins = Math.max(5, Math.round(routeData.duration / 60));
            }
          }
        }
      } catch (err) {
        console.warn("OSRM routing API unavailable, falling back to realistic road curve:", err);
      }

      // Fallback if OSRM returned empty or failed
      if (!pathCoords.length) {
        pathCoords = generateRealisticRoadPath(originLat!, originLng!, destLat!, destLng!);
      }

      if (!isMounted || !mapRef.current || mapRef.current !== currentMap) return;

      try {
        const etaTimeObj = new Date(Date.now() + calculatedMins * 60 * 1000);
        const etaClockTime = etaTimeObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        setActiveRouteInfo({
          destName,
          estimatedKm: calculatedKm,
          etaMins: calculatedMins,
          etaClockTime,
          routeNotes,
          originName,
          driverName: activeDriverName
        });

        // Draw main polyline on real roads
        const polyline = L.polyline(pathCoords, {
          color: '#2563eb',
          weight: 5,
          opacity: 0.85,
          lineCap: 'round',
          lineJoin: 'round'
        }).addTo(currentMap);
        routePolylineRef.current = polyline;

        // Draw glowing dashes over route
        const glowLine = L.polyline(pathCoords, {
          color: '#60a5fa',
          weight: 2,
          dashArray: '8, 8',
          opacity: 0.95
        }).addTo(currentMap);
        routeGlowRef.current = glowLine;

        // Custom Origin Pin
        const originIcon = L.divIcon({
          className: 'custom-route-origin-pin',
          html: `
            <div class="flex flex-col items-center">
              <div class="bg-blue-600 text-white px-2 py-1 rounded-lg border-2 border-white shadow-lg text-[10px] font-black whitespace-nowrap flex items-center gap-1">
                <span>📍</span>
                <span>${originName}</span>
              </div>
              <div class="w-2 h-2 bg-blue-600 border border-white -mt-1 rotate-45"></div>
            </div>
          `,
          iconSize: [160, 32],
          iconAnchor: [80, 32]
        });
        const originMarker = L.marker([originLat!, originLng!], { icon: originIcon, zIndexOffset: 800 }).addTo(currentMap);
        routeOriginMarkerRef.current = originMarker;

        // Custom Destination Pin
        const destIcon = L.divIcon({
          className: 'custom-route-dest-pin animate-bounce',
          html: `
            <div class="flex flex-col items-center">
              <div class="bg-emerald-600 text-white px-2.5 py-1 rounded-lg border-2 border-white shadow-xl text-[10px] font-black whitespace-nowrap flex items-center gap-1">
                <span>🏁</span>
                <span>${destName}</span>
              </div>
              <div class="w-2 h-2 bg-emerald-600 border border-white -mt-1 rotate-45"></div>
            </div>
          `,
          iconSize: [160, 32],
          iconAnchor: [80, 32]
        });
        const destMarker = L.marker([destLat!, destLng!], { icon: destIcon, zIndexOffset: 900 }).addTo(currentMap);
        routeDestMarkerRef.current = destMarker;

        // Auto fit bounds around real road path
        const bounds = L.latLngBounds([[originLat!, originLng!], [destLat!, destLng!]]);
        currentMap.fitBounds(bounds, { padding: [45, 45] });
      } catch (e) {
        console.warn('fitBounds / route render error:', e);
      }
    };

    loadAndRenderRoute();

    return () => {
      isMounted = false;
      try {
        if (routePolylineRef.current && currentMap) currentMap.removeLayer(routePolylineRef.current);
      } catch (e) {}
      routePolylineRef.current = null;

      try {
        if (routeGlowRef.current && currentMap) currentMap.removeLayer(routeGlowRef.current);
      } catch (e) {}
      routeGlowRef.current = null;

      try {
        if (routeOriginMarkerRef.current && currentMap) currentMap.removeLayer(routeOriginMarkerRef.current);
      } catch (e) {}
      routeOriginMarkerRef.current = null;

      try {
        if (routeDestMarkerRef.current && currentMap) currentMap.removeLayer(routeDestMarkerRef.current);
      } catch (e) {}
      routeDestMarkerRef.current = null;
    };
  }, [leafletReady, previewDestination, folios, destinations, driverLocations, costCenters, deviceLocation]);

  // Generate HTML for Leaflet Popup
  const getPopupHTML = (driver: DriverLocation) => {
    const isSim = driver.id.includes('sim-');
    const totalCount = driver.folioIds?.length || driver.encapsulatedFolios?.length || (driver.folioId ? 1 : 0);

    let encapsulatedHTML = '';

    if (driver.encapsulatedFolios && driver.encapsulatedFolios.length > 0) {
      encapsulatedHTML = `
        <div class="mt-2 space-y-1">
          <div class="text-[10px] font-bold text-slate-800 flex items-center justify-between">
            <span>📦 Carga Encapsulada (${driver.encapsulatedFolios.length}):</span>
            <span class="bg-indigo-100 text-indigo-700 px-1.5 py-0.2 rounded text-[8px] font-mono font-extrabold uppercase">Ruta Chofer</span>
          </div>
          <div class="space-y-1 max-h-36 overflow-y-auto pr-1">
            ${driver.encapsulatedFolios.map(f => `
              <div class="bg-slate-50 p-1.5 rounded border border-slate-200 text-[9.5px]">
                <div class="font-bold text-slate-900 flex justify-between items-center">
                  <span class="font-mono text-indigo-700">Folio #${f.folioId}</span>
                  ${f.totalPieces ? `<span class="text-brand-red font-mono font-black">${f.totalPieces} pzas</span>` : ''}
                </div>
                ${f.clientName ? `<div class="text-slate-600 truncate">🏢 ${f.clientName}</div>` : ''}
                ${f.destination ? `<div class="text-slate-500 text-[8.5px] truncate">📍 ${f.destination}</div>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      `;
    } else if (driver.folioIds && driver.folioIds.length > 0) {
      encapsulatedHTML = `
        <div class="mt-2 bg-slate-50 p-1.5 rounded border border-slate-200 text-[10px]">
          <strong>📋 Folios Encapsulados:</strong>
          <div class="flex flex-wrap gap-1 mt-1 font-mono font-bold text-indigo-700">
            ${driver.folioIds.map(id => `<span class="bg-white px-1.5 py-0.5 rounded border border-slate-200">#${id}</span>`).join('')}
          </div>
        </div>
      `;
    } else {
      encapsulatedHTML = `
        <div class="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-gray-500 mt-1">
          <div><strong>📋 Folio:</strong></div>
          <div class="font-mono text-right font-bold text-slate-700">${driver.folioId || 'Sin folio asignado'}</div>
        </div>
      `;
    }

    return `
      <div class="p-2 font-sans space-y-1.5 text-slate-900 min-w-[220px]">
        <div class="flex items-center justify-between border-b border-gray-100 pb-1.5 gap-2">
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0"></span>
            <h5 class="font-black text-xs tracking-tight text-slate-900 uppercase leading-none">${driver.driverName}</h5>
          </div>
          ${totalCount > 1 ? `
            <span class="bg-indigo-600 text-white font-mono text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              📦 ${totalCount} Folios
            </span>
          ` : ''}
        </div>

        <div class="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-gray-600 bg-slate-50/70 p-1.5 rounded-md border border-slate-100">
          <div><strong>🚚 Camión / Placas:</strong></div>
          <div class="font-mono text-right font-bold text-slate-800">${driver.plateNumber || 'TRK-SNF'}</div>
          
          <div><strong>⚡ Velocidad:</strong></div>
          <div class="font-bold text-right text-brand-red">${driver.speed} km/h</div>
          
          <div><strong>🕒 Actualizado:</strong></div>
          <div class="text-right text-gray-400 font-mono text-[8.5px]">${new Date(driver.lastUpdated).toLocaleTimeString()}</div>
        </div>

        ${encapsulatedHTML}

        ${driver.statusText ? `
          <div class="mt-1 bg-amber-50 p-1.5 rounded-md border border-amber-200/80 text-[9px] text-amber-900 font-medium">
            🚩 <strong>Status:</strong> ${driver.statusText}
          </div>
        ` : ''}
        ${isSim ? `
          <span class="mt-1 block text-center text-[8px] bg-slate-900 text-slate-300 rounded font-mono uppercase tracking-wider py-0.5">Camión Simulado</span>
        ` : ''}
      </div>
    `;
  };

  // 5. DRIVER GPS GEOLOCATION CAPTURE (watchPosition)
  const toggleLocationSharing = async () => {
    if (isSharingLive) {
      // STOP SHARING
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setIsSharingLive(false);
      setErrorMessage(null);
      // Delete from db
      const driverId = currentUser.email || currentUser.uid;
      await dataService.deleteDriverLocation(driverId);
      return;
    }

    // START SHARING
    if (!navigator.geolocation) {
      setErrorMessage('Su navegador no soporta geolocalización.');
      return;
    }

    setErrorMessage(null);

    // Prompt for permission and watch
    const successCallback = async (position: GeolocationPosition) => {
      const { latitude, longitude, speed } = position.coords;
      const driverId = currentUser.email || currentUser.uid;

      // Translate speed from m/s to km/h, fallback to 0
      const currentSpeed = speed ? Math.round(speed * 3.6) : 0;

      const updatedLoc: DriverLocation = {
        id: driverId,
        driverName: currentUser.name,
        driverEmail: currentUser.email,
        latitude,
        longitude,
        speed: currentSpeed,
        lastUpdated: new Date().toISOString(),
        active: true,
        plateNumber: plateNumber,
        folioId: selectedFolioId || undefined,
        statusText: `Compartiendo desde dispositivo del chofer`
      };

      await dataService.saveDriverLocation(updatedLoc);
      setSelectedDriver(updatedLoc);
      setIsSharingLive(true);
    };

    const errorCallback = (error: GeolocationPositionError) => {
      console.warn("GPS WatchPosition Warning (expected in sandboxed or local dev environments):", error.message || error);
      let desc = 'No se pudo capturar la ubicación de GPS.';
      if (error.code === error.PERMISSION_DENIED) {
        desc = 'Permiso denegado por el usuario o bloqueado por el navegador. Habilite el acceso GPS para poder compartir ubicación.';
      } else if (error.code === error.TIMEOUT) {
        desc = 'Se agotó el tiempo de espera para obtener la señal GPS.';
      }
      setErrorMessage(desc);
      setIsSharingLive(false);
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    // Begin watchPosition with high accuracy
    const watchId = navigator.geolocation.watchPosition(successCallback, errorCallback, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 0
    });

    watchIdRef.current = watchId;
    // Update initial UI state immediately
    setIsSharingLive(true);
  };

  // 6. SIMULATION: HIGHWAY MONTERREY-SALTILLO
  const toggleHighwaySimulation = async () => {
    if (isSimulatingHighway) {
      clearInterval(highwaySimIntervalRef.current);
      setIsSimulatingHighway(false);
      await dataService.deleteDriverLocation('sim-highway');
      return;
    }

    setHighwaySimIndex(0);
    setIsSimulatingHighway(true);

    const stepSimulation = async (index: number) => {
      const point = HIGHWAY_ROUTE_MOCK[index];
      const mockDriver: DriverLocation = {
        id: 'sim-highway',
        driverName: 'Simeón Juárez (Simulador Chofer Coahuila)',
        driverEmail: 'simeon.chofer@grupoisn.com',
        latitude: point.lat,
        longitude: point.lng,
        speed: index === 0 || index === HIGHWAY_ROUTE_MOCK.length - 1 ? 0 : 85 + Math.round(Math.random() * 15),
        lastUpdated: new Date().toISOString(),
        active: true,
        plateNumber: 'TRK-105-COA',
        folioId: 'F-102501',
        statusText: point.status
      };

      await dataService.saveDriverLocation(mockDriver);

      // Move index forward
      const nextIndex = (index + 1) % HIGHWAY_ROUTE_MOCK.length;
      setHighwaySimIndex(nextIndex);
    };

    // Run first step immediately
    await stepSimulation(0);

    // Start interval
    highwaySimIntervalRef.current = setInterval(() => {
      setHighwaySimIndex((prevIndex) => {
        stepSimulation(prevIndex);
        return (prevIndex + 1) % HIGHWAY_ROUTE_MOCK.length;
      });
    }, 4500);
  };

  // 7. SIMULATION: LOCAL ESCOBEDO-GUADALUPE
  const toggleLocalSimulation = async () => {
    if (isSimulatingLocal) {
      clearInterval(localSimIntervalRef.current);
      setIsSimulatingLocal(false);
      await dataService.deleteDriverLocation('sim-local');
      return;
    }

    setLocalSimIndex(0);
    setIsSimulatingLocal(true);

    const stepSimulation = async (index: number) => {
      const point = LOCAL_ROUTE_MOCK[index];
      const mockDriver: DriverLocation = {
        id: 'sim-local',
        driverName: 'Felipe Reyes (Simulador Chofer NL)',
        driverEmail: 'felipe.reyes@grupoisn.com',
        latitude: point.lat,
        longitude: point.lng,
        speed: index === 0 || index === LOCAL_ROUTE_MOCK.length - 1 ? 0 : 40 + Math.round(Math.random() * 10),
        lastUpdated: new Date().toISOString(),
        active: true,
        plateNumber: 'TRK-294-NL',
        folioId: 'F-102502',
        statusText: point.status
      };

      await dataService.saveDriverLocation(mockDriver);

      // Move index forward
      const nextIndex = (index + 1) % LOCAL_ROUTE_MOCK.length;
      setLocalSimIndex(nextIndex);
    };

    // Run first step immediately
    await stepSimulation(0);

    // Start interval
    localSimIntervalRef.current = setInterval(() => {
      setLocalSimIndex((prevIndex) => {
        stepSimulation(prevIndex);
        return (prevIndex + 1) % LOCAL_ROUTE_MOCK.length;
      });
    }, 4500);
  };

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      if (highwaySimIntervalRef.current) clearInterval(highwaySimIntervalRef.current);
      if (localSimIntervalRef.current) clearInterval(localSimIntervalRef.current);
    };
  }, []);

  // Listen for Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Invalidate map size on fullscreen transition to prevent rendering glitches
  useEffect(() => {
    let timer: any = null;
    if (mapRef.current) {
      timer = setTimeout(() => {
        try {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
        } catch (e) {}
      }, 350);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isFullscreen]);

  // Auto invalidateSize when container resizes or turns visible (mobile tab switch/split)
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const observer = new ResizeObserver(() => {
      try {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      } catch (e) {}
    });
    observer.observe(mapContainerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [leafletReady]);

  // Focus and trace user real-time location (using browser/device GPS signal)
  const focusOnRealTimeLocation = () => {
    if (!navigator.geolocation) {
      setErrorMessage('Su navegador no soporta geolocalización.');
      return;
    }
    setErrorMessage(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (mapRef.current) {
          try {
            mapRef.current.setView([latitude, longitude], 15, { animate: true, duration: 1.5 });
            
            // Add a temporary glowing pulse marker for user's actual live GPS position
            const L = (window as any).L;
            if (L) {
              const gpsIcon = L.divIcon({
                className: 'custom-gps-focus-icon relative flex items-center justify-center',
                html: `
                  <div class="relative flex items-center justify-center w-[40px] h-[40px]">
                    <div class="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
                    <div class="w-4.5 h-4.5 bg-blue-600 border-2 border-white rounded-full shadow-lg"></div>
                  </div>
                `,
                iconSize: [40, 40],
                iconAnchor: [20, 20]
              });
              if (markersRef.current['user-gps-focus'] && markersRef.current['user-gps-focus']._map) {
                markersRef.current['user-gps-focus'].setLatLng([latitude, longitude]);
              } else {
                markersRef.current['user-gps-focus'] = L.marker([latitude, longitude], { icon: gpsIcon })
                  .addTo(mapRef.current)
                  .bindPopup('<div class="p-1 font-sans text-xs font-bold text-slate-800">📍 Tu Ubicación Actual (GPS)</div>');
              }
              if (markersRef.current['user-gps-focus'] && markersRef.current['user-gps-focus']._map) {
                markersRef.current['user-gps-focus'].openPopup();
              }
            }
          } catch (e) {
            console.warn('focusOnRealTimeLocation error:', e);
          }
        }
      },
      (error) => {
        console.warn("GPS Position Fetch error:", error);
        setErrorMessage('No se pudo capturar su ubicación en tiempo real. Habilite el acceso de GPS en su navegador.');
      },
      { enableHighAccuracy: true }
    );
  };

  const flyToCostCenter = (rawCc: CostCenter) => {
    const cc = sanitizeCostCenterCoordinates(rawCc);
    if (!mapRef.current || !cc.lat || !cc.lng) return;
    try {
      mapRef.current.setView([cc.lat, cc.lng], 13, { animate: true, duration: 1.2 });
      if (centerMarkersRef.current[cc.id] && centerMarkersRef.current[cc.id]._map) {
        centerMarkersRef.current[cc.id].openPopup();
      }
    } catch (e) {
      console.warn('flyToCostCenter error:', e);
    }
  };

  if (hideSidebarConsole) {
    return (
      <div className="w-full h-full relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
        {!leafletReady ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <RefreshCw className="h-6 w-6 text-slate-400 animate-spin" />
            <p className="text-xs font-bold text-slate-500">Cargando trazado de ruta en tiempo real...</p>
          </div>
        ) : (
          <>
            <div ref={mapContainerRef} className="w-full h-full" style={{ zIndex: 10 }} />
            
            {/* Active Route & ETA Card Overlay */}
            {activeRouteInfo && (
              <div className="absolute top-3 left-3 z-[1000] bg-slate-900/95 text-white p-3 rounded-2xl border border-slate-700 shadow-2xl backdrop-blur-md max-w-[260px] space-y-1.5 font-sans animate-fade-in pointer-events-auto">
                <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Navigation className="h-3.5 w-3.5 animate-spin" style={{ animationDuration: '6s' }} />
                    <span className="text-[11px] font-black uppercase tracking-wider">Ruta & ETA GPS</span>
                  </div>
                  <span className="bg-emerald-500/20 text-emerald-300 font-mono text-[8px] font-extrabold px-1.5 py-0.5 rounded-full border border-emerald-500/30">
                    EN VIVO
                  </span>
                </div>

                <div className="space-y-1 text-[11px]">
                  <div className="flex items-center justify-between text-slate-300">
                    <span className="text-slate-400">🏁 Destino:</span>
                    <span className="font-bold text-white truncate max-w-[150px]">{activeRouteInfo.destName}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-1.5 bg-blue-950/80 p-2 rounded-xl border border-blue-800/60 mt-1">
                    <div>
                      <p className="text-[8px] text-blue-300 font-bold uppercase tracking-wider">Llegada Est. (ETA)</p>
                      <p className="text-xs font-black text-white font-mono flex items-center gap-1">
                        <Clock className="h-3 w-3 text-blue-400" />
                        {activeRouteInfo.etaMins} mins
                      </p>
                    </div>
                    <div className="text-right border-l border-blue-800/60 pl-1.5">
                      <p className="text-[8px] text-blue-300 font-bold uppercase tracking-wider">Hora Llegada</p>
                      <p className="text-[11px] font-bold text-emerald-400 font-mono">~{activeRouteInfo.etaClockTime}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[10px] pt-0.5">
                    <span className="text-slate-400">🛣️ Recorrido:</span>
                    <span className="font-mono font-bold text-blue-400">{activeRouteInfo.estimatedKm} km</span>
                  </div>
                </div>
              </div>
            )}

            <div className="absolute top-3 right-3 z-[1000] flex gap-2 bg-slate-900/90 p-1 rounded-lg border border-slate-700 shadow-md">
              <button
                type="button"
                onClick={focusOnRealTimeLocation}
                className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-xs transition-all flex items-center gap-1 text-[10px] font-bold cursor-pointer"
                title="Mi Ubicación GPS"
              >
                <Compass className="h-3 w-3 animate-pulse" />
                <span>GPS</span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" id="logistics-map-system">
      
      {/* SIDEBAR CONSOLE */}
      <div className="lg:col-span-1 space-y-5">
        
        {/* DRIVER CONSOLE PANEL */}
        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-tiny space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
            <Radio className="h-4 w-4 text-brand-red animate-pulse" />
            <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight">Consola del Chofer (GPS)</h4>
          </div>

          <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
            ¿Eres conductor de una unidad? Vincula tu ruta a un folio activo y activa tu geolocalización en tiempo real. Funciona igual que la ubicación en vivo de WhatsApp.
          </p>

          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block font-mono">Folio Activo Sincronizado</label>
              <select
                value={selectedFolioId}
                onChange={(e) => setSelectedFolioId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-[11px] font-bold text-slate-700 focus:outline-hidden focus:border-brand-red"
              >
                <option value="">-- Sin Folio Asociado --</option>
                {activeFoliosForLogistics.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.id} - {f.clientName} ({f.status})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block font-mono">Placas del Camión</label>
              <input
                type="text"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                placeholder="TRK-123-NL"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-[11px] font-bold text-slate-700 font-mono uppercase focus:outline-hidden focus:border-brand-red"
              />
            </div>

            <button
              onClick={toggleLocationSharing}
              className={`w-full py-2.5 px-3 rounded-lg text-[11px] font-black uppercase transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs ${
                isSharingLive 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white animate-pulse' 
                  : 'bg-brand-red hover:bg-brand-red-hover text-white'
              }`}
            >
              {isSharingLive ? (
                <>
                  <Square className="h-3.5 w-3.5 fill-current" />
                  Detener Compartir GPS
                </>
              ) : (
                <>
                  <Navigation className="h-3.5 w-3.5 rotate-45 fill-current" />
                  Compartir mi GPS en Vivo
                </>
              )}
            </button>

            {isSharingLive && (
              <div className="p-2 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-[9px] text-emerald-800 font-bold font-mono">🔴 GPS TRANSMITIENDO SEÑAL ACTIVA</span>
              </div>
            )}

            {errorMessage && (
              <div className="p-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-[10px] font-medium flex items-start gap-1.5 leading-snug">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        </div>

        {/* SEDES / COST CENTERS SELECTOR LIST */}
        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-tiny space-y-2">
          <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-wider block font-mono border-b border-slate-100 pb-1.5">🎯 Sedes Operativas Registradas</h4>
          <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
            {costCenters.map(sanitizeCostCenterCoordinates).map(cc => (
              <button
                key={cc.id}
                onClick={() => flyToCostCenter(cc)}
                className="w-full text-left p-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-[10px] font-bold text-slate-700 border border-slate-150 transition-all flex items-center justify-between cursor-pointer"
              >
                <span className="truncate">📍 {cc.name}</span>
                <span className="text-[8px] px-1.5 py-0.2 bg-slate-200 text-slate-600 rounded-xs font-mono">{cc.code}</span>
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* MAP VIEWPORT & TRUCK STATUS LIST */}
      <div className="lg:col-span-3 space-y-4">
        
        {/* TOP COUNTERS SUMMARY */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-tiny flex items-center gap-3">
            <div className="p-2 bg-slate-100 text-slate-700 rounded-lg">
              <Truck className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Camiones Activos</p>
              <h5 className="text-sm font-black text-slate-800">{activeTransitDrivers.length}</h5>
            </div>
          </div>

          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-tiny flex items-center gap-3">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <Radio className="h-4 w-4 animate-pulse" />
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Conductores en Vivo</p>
              <h5 className="text-sm font-black text-emerald-600">
                {activeTransitDrivers.filter(d => !d.id.includes('sim-')).length}
              </h5>
            </div>
          </div>

          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-tiny flex items-center gap-3">
            <div className="p-2 bg-slate-100 text-slate-700 rounded-lg">
              <MapPin className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Puntos Clave</p>
              <h5 className="text-sm font-black text-slate-800">{costCenters.length}</h5>
            </div>
          </div>
        </div>

        {/* MAP CONTAINER ROW */}
        <div className={`bg-white p-3 border border-slate-200/80 shadow-tiny flex flex-col md:flex-row gap-4 transition-all duration-300 ${
          isFullscreen 
            ? 'fixed inset-0 z-[5000] h-screen w-screen p-4 rounded-none bg-white' 
            : 'rounded-2xl h-[500px]'
        }`}>
          
          {/* MAP CANVAS CONTAINER */}
          <div className="flex-1 rounded-xl overflow-hidden border border-slate-200 relative bg-slate-50 h-full">
            {!leafletReady ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="h-6 w-6 text-slate-400 animate-spin" />
                <p className="text-xs font-bold text-slate-500">Cargando visualización geográfica...</p>
              </div>
            ) : (
              <>
                <div ref={mapContainerRef} className="w-full h-full" style={{ zIndex: 10 }} />
                
                {/* Active Route & ETA Card Overlay */}
                {activeRouteInfo && (
                  <div className="absolute top-3 left-3 z-[1000] bg-slate-900/95 text-white p-3 rounded-2xl border border-slate-700 shadow-2xl backdrop-blur-md max-w-[270px] space-y-1.5 font-sans animate-fade-in pointer-events-auto">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                      <div className="flex items-center gap-1.5 text-emerald-400">
                        <Navigation className="h-3.5 w-3.5 animate-spin" style={{ animationDuration: '6s' }} />
                        <span className="text-[11px] font-black uppercase tracking-wider">Ruta & ETA GPS</span>
                      </div>
                      <span className="bg-emerald-500/20 text-emerald-300 font-mono text-[8px] font-extrabold px-1.5 py-0.5 rounded-full border border-emerald-500/30">
                        EN VIVO
                      </span>
                    </div>

                    <div className="space-y-1 text-[11px]">
                      <div className="flex items-center justify-between text-slate-300">
                        <span className="text-slate-400">🏁 Destino:</span>
                        <span className="font-bold text-white truncate max-w-[160px]">{activeRouteInfo.destName}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-slate-300">
                        <span className="text-slate-400">📍 Origen/Punto:</span>
                        <span className="font-semibold text-slate-200 truncate max-w-[160px]">{activeRouteInfo.originName}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-1.5 bg-blue-950/80 p-2 rounded-xl border border-blue-800/60 mt-1">
                        <div>
                          <p className="text-[8px] text-blue-300 font-bold uppercase tracking-wider">Llegada Est. (ETA)</p>
                          <p className="text-xs font-black text-white font-mono flex items-center gap-1">
                            <Clock className="h-3 w-3 text-blue-400" />
                            {activeRouteInfo.etaMins} mins
                          </p>
                        </div>
                        <div className="text-right border-l border-blue-800/60 pl-1.5">
                          <p className="text-[8px] text-blue-300 font-bold uppercase tracking-wider">Hora Llegada</p>
                          <p className="text-[11px] font-bold text-emerald-400 font-mono">~{activeRouteInfo.etaClockTime}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[10px] pt-0.5">
                        <span className="text-slate-400">🛣️ Recorrido:</span>
                        <span className="font-mono font-bold text-blue-400">{activeRouteInfo.estimatedKm} km</span>
                      </div>

                      {activeRouteInfo.routeNotes && (
                        <p className="text-[9px] text-slate-300 italic bg-slate-800/80 p-1.5 rounded-lg border border-slate-700/60">
                          Vía: {activeRouteInfo.routeNotes}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* FLOATING CONTROLS PANEL */}
                <div className="absolute top-3 right-3 z-[1000] flex flex-col sm:flex-row gap-2 bg-slate-900/90 p-1.5 rounded-xl border border-slate-700 shadow-lg backdrop-blur-xs">
                  <button
                    type="button"
                    onClick={focusOnRealTimeLocation}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 text-[10px] font-bold cursor-pointer"
                    title="Ver Mi Ubicación GPS en Tiempo Real"
                  >
                    <Compass className="h-3.5 w-3.5 animate-pulse" />
                    <span>Mi Ubicación Real (GPS)</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* RIGHT PANELS: ACTIVE VEHICLES LIST */}
          <div className="w-full md:w-[220px] bg-slate-50 rounded-xl border border-slate-200 p-3 flex flex-col justify-between space-y-3 shrink-0">
            <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block font-mono border-b border-slate-200 pb-1 flex items-center gap-1">
                <Users className="h-3 w-3 text-slate-400" />
                Camiones en Tránsito
              </span>

              <div className="space-y-2 overflow-y-auto flex-1 pr-0.5">
                {activeTransitDrivers.length === 0 ? (
                  <div className="h-36 flex flex-col items-center justify-center text-center p-3 text-slate-400 gap-1.5">
                    <Truck className="h-6 w-6 opacity-40 text-slate-400" />
                    <p className="text-[10px] font-bold">No hay vehículos transmitiendo en este momento.</p>
                    <p className="text-[9px] leading-relaxed text-slate-400 font-medium">Use la consola izquierda o consulte los folios activos para ver el rastreo.</p>
                  </div>
                ) : (
                  activeTransitDrivers.map((driver) => {
                    const isSelected = selectedDriver?.id === driver.id;
                    return (
                      <div
                        key={driver.id}
                        onClick={() => setSelectedDriver(isSelected ? null : driver)}
                        className={`p-2 rounded-lg border text-[10px] transition-all cursor-pointer flex flex-col gap-1.5 ${
                          isSelected 
                            ? 'bg-slate-900 border-slate-900 text-white shadow-xs' 
                            : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between font-bold">
                          <span className="truncate max-w-[120px]">{driver.driverName.split(' ')[0]} {driver.driverName.split(' ')[1] || ''}</span>
                          <span className="font-mono text-[8px] bg-slate-100 text-slate-800 px-1 py-0.2 rounded-sm select-all">
                            {driver.plateNumber}
                          </span>
                        </div>

                        {driver.folioId && (
                          <div className={`text-[8px] font-bold font-mono px-1 py-0.2 rounded-xs w-fit ${isSelected ? 'bg-slate-800 text-emerald-400' : 'bg-slate-100 text-brand-red'}`}>
                            {driver.folioId}
                          </div>
                        )}

                        <div className="flex items-center justify-between text-[8px] font-medium font-mono pt-0.5 border-t border-dashed border-slate-200/50">
                          <span className="flex items-center gap-0.5 text-slate-500">
                            <Activity className="h-2.5 w-2.5 text-slate-400" />
                            {driver.speed} km/h
                          </span>
                          <span className="flex items-center gap-0.5 text-slate-500">
                            <Clock className="h-2.5 w-2.5 text-slate-400" />
                            {new Date(driver.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* MAP LAYOUT HINT */}
            <div className="bg-white p-2 rounded-lg border border-slate-200 text-[8px] text-slate-500 leading-normal flex items-start gap-1">
              <span className="text-slate-400 font-bold">💡</span>
              <span>Haga clic sobre un camión para seguir su trayectoria y ver información extendida.</span>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
